/**
 * QuickNotePanel — Peek panel for creating pages and databases quickly.
 */
import React, { useState } from 'react';
import { Box, Group, Text, TextInput, Button, Stack, ActionIcon } from '@mantine/core';
import { useWorkspace } from '../../../core/workspace/WorkspaceContext';
import { usePeek } from './PeekManager';

export const QuickNotePanel: React.FC = () => {
  const [note, setNote] = useState('');
  const { createPage, selectPage } = useWorkspace();
  const { closePeek } = usePeek();

  const handleSave = async (kind: 'page' | 'database' = 'page') => {
    if (!note.trim()) return;
    try {
      const newPage = await createPage(note.trim(), undefined, kind);
      await selectPage(newPage.id);
      setNote('');
      closePeek();
    } catch (e) { console.error('Failed to create note:', e); }
  };

  return (
    <Box style={{ position: 'fixed', top: 50, right: 0, width: 320, height: 'calc(100vh - 50px)', backgroundColor: 'var(--fc-bg-primary)', boxShadow: '-2px 0 12px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', zIndex: 100 }}>
      <Box p="md" style={{ borderBottom: '1px solid var(--fc-border-primary)' }}>
        <Group justify="space-between" align="center">
          <Text fw={600}>Quick Note</Text>
          <ActionIcon variant="subtle" size="sm" onClick={() => closePeek()}>✕</ActionIcon>
        </Group>
      </Box>
      <Box p="md" style={{ flex: 1 }}>
        <TextInput placeholder="Page or database title..." size="sm" mb="md" value={note}
          onChange={e => setNote(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSave('page')}
          styles={{ input: { fontSize: 14 } }} autoFocus />
        <Text size="xs" c="dimmed">Type a title, then pick a kind below.</Text>
      </Box>
      <Box p="md" style={{ borderTop: '1px solid var(--fc-border-primary)' }}>
        <Stack gap="xs">
          <Button variant="filled" color="blue" fullWidth onClick={() => handleSave('page')} disabled={!note.trim()}>Create Page</Button>
          <Button variant="light" color="grape" fullWidth onClick={() => handleSave('database')} disabled={!note.trim()}>Create Database</Button>
        </Stack>
      </Box>
    </Box>
  );
};